import type { LucideIcon } from 'lucide-react';

type Tone = 'default' | 'wood' | 'success' | 'warning' | 'danger' | 'info' | 'ippc';

interface StatCardProps {
  label: string;
  value: string | number;
  description?: string;
  icon?: LucideIcon;
  /** 保留旧 color 参数做 API 兼容；内部映射到 tone。不推荐新代码再用 color。 */
  color?: 'orange' | 'emerald' | 'blue' | 'amber' | 'slate' | 'red';
  tone?: Tone;
  /** 数值前缀（如 "¥"）。与 value 同 display 字号渲染。*/
  prefix?: string;
}

// 数值底色：默认 ironwood 深木；按 tone 调成语义色
const valueToneClasses: Record<Tone, string> = {
  default: 'text-ironwood',
  wood:    'text-wood-700',
  success: 'text-success',
  warning: 'text-warning',
  danger:  'text-danger',
  info:    'text-info',
  ippc:    'text-ippc-700',
};

// 旧 color 参数到 tone 的映射（兼容层，Batch 4 清完页面后删）
const legacyColorToTone: Record<NonNullable<StatCardProps['color']>, Tone> = {
  orange:  'wood',
  emerald: 'success',
  blue:    'info',
  amber:   'warning',
  slate:   'default',
  red:     'danger',
};

/**
 * KPI 统计卡片。
 *
 * 设计系统要求（DESIGN.md §10）：
 * - 圆角 md = 8px
 * - 边框 birch-200 暖色
 * - 数值用 font-mono + tabular-nums，display 字号
 * - 图标用深木色描边，不用彩色圆底（去 AI 味）
 */
export default function StatCard({ label, value, description, icon: Icon, color, tone, prefix }: StatCardProps) {
  const effectiveTone: Tone = tone ?? (color ? legacyColorToTone[color] : 'default');

  return (
    <div className="rounded-md border border-birch-200 bg-white p-5 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-oak-500">{label}</div>
          <div className={`mt-2 font-mono text-3xl font-semibold tabular-nums tracking-tight ${valueToneClasses[effectiveTone]}`}>
            {prefix ? <span className="mr-0.5 text-oak-500">{prefix}</span> : null}
            {value}
          </div>
          {description ? (
            <div className="mt-2 text-xs leading-5 text-oak-500">{description}</div>
          ) : null}
        </div>
        {Icon ? (
          <div className="flex-shrink-0 text-oak-500">
            <Icon className="h-5 w-5" strokeWidth={1.75} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
